/**
 * @changelog
 *
 * 2026-02-26 (0.1.0) (jhoward)
 *   - Import PropertyCode type.
 *   - Add mainProperty to state as PropertyCode | null.
 *
 * 2026-02-26 (0.2.0) (jhoward)
 *   - Add notificationsComponent, isOverlayActive, isLoadingSpinnerActive, overlayText to state.
 *   - Add setNewSnack, closeCurrentSnack, showLoader, hideLoader actions for notifications and overlays.
 *
 * 2026-02-27 (0.3.0) (jhoward)
 *   - Add user and isLoaded to app state.
 *   - Add updateStateFromServer action.
 *   - Add getAppPath helper and baseUrl/assetUrl state.
 *   - Refactor to TypeScript interfaces for state/actions.
 *
 * 2026-02-28 (0.4.0) (jhoward)
 *   - Add account.offers state and isFetchOffersInProgress flag.
 *   - Add getAccountOffers and getAccountOffer actions for fetching offers from server.
 *
 * 2026-03-02 (0.4.1) (jhoward)
 *   - Set overlayText to 'Loading offers...' during offer fetch and clear after completion.
 *
 * 2026-03-11 (0.5.0) (jhoward)
 *   - Add twoFactorAuthRequired and reauthentication state.
 *   - Add winLoss and info to account state.
 *   - Add environment, defaultProperty, appTitle, pageTitle.
 *   - Remove getAccountOffers/getAccountOffer (now handled elsewhere).
 *
 * 2026-03-13 (0.6.0) (jhoward)
 *   - Add offerManagementDialog state for dialog control.
 *
 * 2026-03-13 (0.7.0) (jhoward)
 *   - Add AppStoreActions::getOfferImageUrl for retrieving offer image URLs by size.
 *   - Update AppStore type definitions and actions accordingly.
 *
 * 2026-03-13 (0.7.1) (jhoward)
 *   - Add currentRoute property to AppStoreState and initialize to null.
 *
 * 2026-03-30 (0.7.2) (jhoward)
 *   - Add addSnack action to AppStore for notification handling.
 *
 * 2026-04-09 (0.7.3) (jhoward)
 *   - Add new_pin_required to AppStoreState and default storeState.
 *
 * 2026-08-20 (0.7.4) (jhoward)
 *   - Update updateStateFromServer to copy defaultProperty from server payload into state.
 *
 * 2026-08-27 (0.7.5) (jhoward)
 *   - Widen state.user/AppServerState.user to User | MinimalUser | null to support the pre-2FA
 *     minimal user payload returned by login before full user info is sent after 2FA completes.
 *
 * 2026-09-14 (0.8.0) (jhoward)
 *   - Add maintenanceWindow and maintenanceWindowAnchorMs to AppStoreState, and maintenance_window to
 *     AppServerState, for the scheduled maintenance warning banner. Both sit at the top level rather than inside
 *     `state`, which router/index.ts replaces wholesale on its no-body branch.
 *
 * 2026-10-06 (0.8.1) (jhoward)
 *   - Seed defaultProperty from the default_property-XX class the server puts on <html>, and stop
 *     updateStateFromServer() clearing it. /state answers a logged-out visitor with a 401 carrying only a redirect,
 *     so every guest got null here - on every hostname - and getPropertyOptions() and the other readers had nothing.
 *
 * 2026-10-07 (0.8.2) (jhoward)
 *   - Fix the getPropertyFromDocument() regex, which matched a literal 's' instead of whitespace (\s), so the class
 *     was only found at the very start or end of the class list. Move the helper below the imports.
 *
 * 2026-10-09 (0.9.0) (jhoward)
 *   - Add remembered to AppStoreState, and remembered to AppServerState, so the UI can tell a guest that "remember me"
 *     and "remember this device" are in effect.
 *
 * 2026-10-10 (0.10.0) (jhoward)
 *   - Call the WebAuthn method a Passkey (icon key) on the method tiles, not "Biometric" with a fingerprint - it is
 *     wrong for a Windows PIN or a security key, and the OS prompt itself says passkey. "Authenticator app"
 *     (phonelink_lock) and "Text message" likewise.
 *
 * 2026-10-10 (0.11.0) (jhoward)
 *   - RememberedState carries when the honoured token expires, which method earned it, and the standard and strong
 *     windows, so the notices can say "until {date}" and "90 days with a passkey".
 *
 * 2026-10-15 (0.12.0) (jhoward)
 *   - Add resetAccountState(), which drops everything loaded for the signed-in patron (account info, offers,
 *     win/loss, tournaments, the PIN and offer dialogs, the remembered notices). updateStateFromServer() calls it
 *     when the user changes. Only a successful logout cleared any of it, so after an expired session the next
 *     patron to sign in on the tab was shown the previous patron's account.
 *
 * 2026-10-27 (0.13.0) (jhoward)
 *   - Seed csrfToken from the csrf-token meta tag the server writes into the page. It started empty, so the first
 *     /state (and a signed-out guest's login) carried no _token and the framework fell back to the XSRF-TOKEN cookie.
 *     Production sets its cookies with Domain=opc.peppermillresorts.com, which also covers dev.opc.peppermillresorts.com,
 *     so staging receives two XSRF-TOKEN cookies; when production's came first, every request on staging was a 419
 *     ("Your session has expired") that no reload could clear. Laravel checks _token before the cookie, so a token
 *     from the page's own session makes the duplicate cookie irrelevant.
 *   - updateStateFromServer() keeps the current csrfToken when the payload carries none. A signed-out /state is a 401
 *     whose data is only {redirect}, and the router guard passes that data here, so the seeded token was blanked
 *     before the first real request.
 *
 * 2026-10-28 (0.14.0) (jhoward)
 *   - Add propertyShutoff and propertyShutoffBypass: whether this property's site has been shut off (seeded from the
 *     page's meta tags, kept current from /state and from a PROPERTY_SHUTOFF response), and whether this browser has
 *     opened the administrator's access link. Top level, like maintenanceWindow, because router/index.ts can replace
 *     `state` wholesale.
 */

import type {
  MaterialFormContainerStoreActions,
  MaterialFormContainerStoreState,
} from '@framework/components/material/MaterialFormContainer.vue';
import type {
  PepNotifyOptions,
  PeppermillNotificationsComponent,
} from '@framework/components/PeppermillNotifications.vue';
import type { PeppermillServerState } from '@framework/defs/framework';
import type { PeppermillFetchStoreActions, PeppermillFetchStoreState } from '@framework/helpers/peppermillCustomFetch';
import { defineStore } from 'pinia';
import type { Store } from 'pinia';
import type { RouteLocationNormalized } from 'vue-router';
import { useRouter } from 'vue-router';
import type { OpcAccountInfo } from '@/defs/api/Accountinfo';
import type { OpcImageSize } from '@/defs/api/OpcImage';
import type { OpcOffer } from '@/defs/api/OpcOffer';
import type { OpcTournamentResult } from '@/defs/api/TournamentResult';
import type { OpcWinLossData } from '@/defs/api/WinLossData';
import type { MaintenanceWindowPayload } from '@/defs/MaintenanceWindow';
import type { PropertyCode } from '@/defs/property';
import type { PropertyStatement } from '@/defs/PropertyStatement';
import type { TwoFactorMethods } from '@/defs/TwoFactorAuth';
import type { MinimalUser, User } from '@/defs/user';

//The property the server resolved from the hostname, as it wrote it on <html> (resources/views/app.blade.php).
function getPropertyFromDocument(): PropertyCode | null {
  const match = /(?:^|\s)default_property-(2[ABC])(?:\s|$)/.exec(document.documentElement.className);
  return match ? (match[1] as PropertyCode) : null;
}

//Whether the server wrote the property-shutoff meta tags (resources/views/app.blade.php); /state cannot say for a guest.
function hasMetaFlag(name: string): boolean {
  return '1' === document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content;
}

//The CSRF token for this page's session, as the server wrote it into <head> (resources/views/app.blade.php).
function getCsrfTokenFromDocument(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '';
}

export interface RememberedState {
  /** "Remember Me" was chosen at login. */
  password: boolean;
  /** "Remember this device" was chosen for two-factor, and this browser's cookie is still valid. */
  two_factor: boolean;
  /** How many days the honoured token was issued for. */
  two_factor_days: number;
  /** When the honoured token expires (ISO 8601), or null when none is. */
  two_factor_expires_at?: string | null;
  /** The method that earned the honoured token, or null when none is or it predates recording this. */
  two_factor_method?: TwoFactorMethods | 'recovery' | null;
  /** The window a sent code earns, and the window a passkey or authenticator app earns. */
  days_standard?: number;
  days_strong?: number;
}

export interface AppStoreState extends PeppermillFetchStoreState, MaterialFormContainerStoreState {
  isLoaderActive: boolean;
  mainProperty: PropertyCode | null;
  notificationsComponent: PeppermillNotificationsComponent | null;
  isOverlayActive: boolean;
  isLoadingSpinnerActive: boolean;
  overlayText: string;
  state: {
    isLoaded: boolean;
    user: User | MinimalUser | null;
    twoFactorAuthRequired: boolean;
    reauthenticationRequired: boolean;
    /**
     * Released when the re-authentication dialog closes. The argument says whether the guest actually proved it
     * was them: false means they left instead, and whatever was waiting should settle rather than wait forever.
     */
    reauthenticationCallback: ((wasSatisfied?: boolean) => void) | null;
    new_pin_required: boolean;
  };
  baseUrl: string;
  account: {
    offers: OpcOffer[] | null;
    info: OpcAccountInfo | null;
    winLoss: OpcWinLossData[];
    winLossDate: string | null;
    //Whose letterhead the statement carries. From the API, so the on-screen copy and the PDF agree.
    winLossStatement: PropertyStatement | null;
    tournamentResults: OpcTournamentResult[];
  };
  offerManagementDialog: {
    offer: OpcOffer | null;
  };
  isFetchOffersInProgress: boolean;
  environment: string;
  defaultProperty: PropertyCode | null;
  appTitle: string;
  pageTitle: string;
  currentRoute: RouteLocationNormalized | null;

  /**
   * The scheduled maintenance window, or null when there is nothing to warn about.
   *
   * Top level rather than inside `state` on purpose: router/index.ts replaces `appStore.state` wholesale when the
   * hydration call comes back without a body, so anything parked in there is destroyed on that path.
   */
  maintenanceWindow: MaintenanceWindowPayload | null;
  /**
   * performance.now() at the moment the window payload landed. Monotonic, so the countdown survives a wrong device
   * clock, a daylight-saving step and an NTP correction. Never Date.now().
   */
  maintenanceWindowAnchorMs: number | null;

  /** This property's site is shut off (App\Services\PropertyShutoff). App.vue shows the notice instead of the page. */
  propertyShutoff: boolean;
  /** This browser has opened the administrator's access link, so it is let through a shutoff. */
  propertyShutoffBypass: boolean;

  /**
   * Which "remember" choices are in effect for this sign-in. Top level for the same reason as maintenanceWindow:
   * router/index.ts can replace `state` wholesale.
   */
  remembered: RememberedState;

  twoFactorMethodIcons: {
    email: string;
    sms: string;
    webauthn: string;
    authenticator: string;
  };
  twoFactorMethodLabels: {
    email: string;
    sms: string;
    webauthn: string;
    authenticator: string;
  };
}

type AppStore = Store<'app', AppStoreState, {}, AppStoreActions>;

export interface AppStoreActions extends PeppermillFetchStoreActions, MaterialFormContainerStoreActions {
  getPathByRoute: (this: AppStore, to: { name?: string; params?: {}; query?: {} }) => string;
  getOfferImageUrl: (this: AppStore, offer: OpcOffer, imageSize: OpcImageSize) => string | null;
  addSnack: (this: AppStore, options: PepNotifyOptions) => void;
  resetAccountState: (this: AppStore) => void;
}

export interface AppServerState {
  user: User | MinimalUser | null;
  twoFactorAuthRequired: boolean;
  csrfToken: string;
  honeypot: string;
  defaultProperty: PropertyCode | null;
  maintenance_window: MaintenanceWindowPayload | null;
  propertyShutoff?: boolean;
  propertyShutoffBypass?: boolean;
  remembered?: RememberedState;
}

/**
 * A fresh state object each time.
 *
 * Pinia calls this to build the store's state. Returning a shared module-level object instead - which is what
 * `state: () => storeState` did - hands every caller the same instance, so $reset() resets to whatever the state
 * has already been mutated to, and a second Pinia instance (a test, an SSR render) inherits the first one's data.
 */
function createStoreState(): AppStoreState {
  return {
    isLoaderActive: false,
    mainProperty: null,
    notificationsComponent: null,
    isOverlayActive: false,
    isLoadingSpinnerActive: false,
    overlayText: '',
    state: {
      isLoaded: false,
      user: null,
      twoFactorAuthRequired: false,
      reauthenticationRequired: false,
      reauthenticationCallback: null,
      new_pin_required: false,
    },
    account: {
      offers: null,
      info: null,
      winLoss: [],
      winLossDate: null,
      winLossStatement: null,
      tournamentResults: [],
    },
    offerManagementDialog: {
      offer: null,
    },
    isFetchOffersInProgress: false,
    csrfToken: getCsrfTokenFromDocument(),
    honeypot: '',
    baseUrl: import.meta.env.BASE_URL ? import.meta.env.BASE_URL : '',
    assetUrl: import.meta.env.VITE_ASSET_URL ? import.meta.env.VITE_ASSET_URL : '',
    environment: import.meta.env.VITE_APP_ENV ? import.meta.env.VITE_APP_ENV : 'production',
    defaultProperty: getPropertyFromDocument(),
    appTitle: 'OPC',
    pageTitle: '',
    currentRoute: null,
    maintenanceWindow: null,
    maintenanceWindowAnchorMs: null,
    propertyShutoff: hasMetaFlag('property-shutoff'),
    propertyShutoffBypass: hasMetaFlag('property-shutoff-bypass'),
    remembered: {
      password: false,
      two_factor: false,
      two_factor_days: 30,
      two_factor_expires_at: null,
      two_factor_method: null,
      days_standard: 30,
      days_strong: 90,
    },

    twoFactorMethodIcons: {
      email: 'email',
      sms: 'sms',
      webauthn: 'key',
      authenticator: 'phonelink_lock',
    },
    twoFactorMethodLabels: {
      email: 'Email',
      sms: 'Text message',
      webauthn: 'Passkey',
      authenticator: 'Authenticator app',
    },
  };
}

const storeActions: AppStoreActions = {
  addSnack(this: AppStore, options: PepNotifyOptions): void {
    if (this.notificationsComponent) {
      this.notificationsComponent.addSnack(options);
    }
  },
  setNewSnack(
    this: AppStore,
    duration?: number | null,
    message?: string | null,
    icon?: string | null,
    actionText?: string | null,
    actionLink?: string | null,
    callback?: () => any,
    forceClose?: boolean,
  ): void {
    if (this.notificationsComponent) {
      const options: PepNotifyOptions = {};
      if ('number' === typeof duration) {
        options.duration = duration;
      }
      if ('string' === typeof message) {
        options.message = message;
      }
      if ('string' === typeof icon) {
        options.icon = icon;
      }
      if ('string' === typeof actionText) {
        options.actionText = actionText;
      }
      if ('string' === typeof actionLink) {
        options.actionLink = actionLink;
      }
      if ('function' === typeof callback) {
        options.callback = callback;
      }
      if ('boolean' === typeof forceClose) {
        options.forceClose = forceClose;
      }
      this.notificationsComponent.addSnack(options);
    } else {
      console.warn('notifications component not set');
    }
  },
  closeCurrentSnack(this: AppStore): void {
    if (this.notificationsComponent) {
      this.notificationsComponent.closeCurrentSnack();
    } else {
      console.warn('notifications component not set');
    }
  },

  showLoader(this: AppStore, showLoadingSpinner: boolean = true, overlayText: string = '') {
    this.overlayText = overlayText;
    this.isOverlayActive = true;
    this.isLoadingSpinnerActive = showLoadingSpinner;
  },
  hideLoader(this: AppStore) {
    this.isOverlayActive = false;
    this.isLoadingSpinnerActive = false;
    this.overlayText = '';
  },

  getAppPath(
    this: AppStore,
    relativePath: string,
    replacementValues: {
      [key: string]: string | number;
    } = {},
    isApiCall: boolean = true,
  ): string {
    for (const key in replacementValues) {
      relativePath = relativePath.replace(
        new RegExp('(^|\/){' + key + '}(\/|$)'),
        '$1' + replacementValues[key] + '$2',
      );
      relativePath = relativePath.replace(new RegExp('(^|\/):' + key + '(\/|$)'), '$1' + replacementValues[key] + '$2');
    }
    let output = (this.baseUrl + (isApiCall ? '/api' : '') + relativePath).replace(/\/\//g, '/');
    output = output.substring(output.length - 1) === '/' ? output.substring(0, output.length - 1) : output;
    return output;
  },
  getPathByRoute(this: AppStore, to: { name?: string; params?: {}; query?: {} }): string {
    const route = useRouter().resolve(to);
    const parentPaths = route.matched
      .slice(0, -1)
      .map((r) => r.path)
      .join('');
    return parentPaths + route.fullPath;
  },

  /**
   * Forget everything loaded for the patron who was signed in. Call it whenever who is signed in may have changed:
   * account data is cached here across pages, and nothing else ties it to a user.
   */
  resetAccountState(this: AppStore) {
    const fresh = createStoreState();
    this.account = fresh.account;
    this.offerManagementDialog = fresh.offerManagementDialog;
    this.remembered = fresh.remembered;
    this.state.new_pin_required = false;
  },

  resetLogoutTimers() {},
  updateStateFromServer(serverPayload: (PeppermillServerState & AppServerState) | null) {
    const incomingUserId = serverPayload?.user?.id ?? null;
    if (incomingUserId !== ((this as unknown as AppStore).state.user?.id ?? null)) {
      (this as unknown as AppStore).resetAccountState();
    }

    (this as unknown as AppStore).state.isLoaded = true;
    (this as unknown as AppStore).state.user = serverPayload?.user || null;
    (this as unknown as AppStore).state.twoFactorAuthRequired = serverPayload?.twoFactorAuthRequired !== false;
    //Kept when the payload has none: a signed-out /state answers 401 with only a redirect, and blanking the token
    //seeded from the page sent the next request back to the XSRF-TOKEN cookie.
    (this as unknown as AppStore).csrfToken = serverPayload?.csrfToken || (this as unknown as AppStore).csrfToken;
    (this as unknown as AppStore).honeypot = serverPayload?.honeypot || '';
    (this as unknown as AppStore).defaultProperty =
      serverPayload?.defaultProperty || (this as unknown as AppStore).defaultProperty || getPropertyFromDocument();

    //Only when the payload carries the keys: a signed-out /state is a 401 with just {redirect}, which must not
    //clear what the page's own meta tags said.
    if (serverPayload && 'propertyShutoff' in serverPayload) {
      (this as unknown as AppStore).propertyShutoff = serverPayload.propertyShutoff === true;
      (this as unknown as AppStore).propertyShutoffBypass = serverPayload.propertyShutoffBypass === true;
    }

    if (serverPayload?.remembered) {
      (this as unknown as AppStore).remembered = serverPayload.remembered;
    }

    //Keyed on the property being present rather than truthy, so a payload that simply does not carry the field
    //leaves a window already on screen alone. A real "nothing scheduled" answer sends the key with null.
    if (serverPayload && 'maintenance_window' in serverPayload) {
      (this as unknown as AppStore).maintenanceWindow = serverPayload.maintenance_window ?? null;
      (this as unknown as AppStore).maintenanceWindowAnchorMs = performance.now();
    }
  },

  getOfferImageUrl(this: AppStore, offer: OpcOffer, imageSize: OpcImageSize): string | null {
    for (let i = 0, l = offer._IMAGES.length; i < l; i++) {
      if (offer._IMAGES[i].OFFER_IMAGE_WIDTH_KEY === imageSize) {
        return (
          '/storage/offer-images/' +
          offer._IMAGES[i].OFFER_IMAGE_HASH +
          '-' +
          offer._IMAGES[i].OFFER_IMAGE_WIDTH_KEY +
          '.jpg'
        );
      }
    }

    return null;
  },
};

const useAppStore = defineStore('app', {
  state: createStoreState,
  actions: storeActions,
});

export default useAppStore;
